import { Color } from "config/colors";
import { Modal } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { parseWorkTime } from "utils/utils";
import { Text, H3 } from "components/Typography/text.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { Worker, TipDetails, PayoutType } from "model/Tip";
import {
  WorkerCell,
  TipModalBody,
  TipButtonStyled,
  WorkerContainer,
  DetailsContainer,
} from "../tips.styled";
import { useMemo, Fragment, useEffect, useState } from "react";
import { FontFamily } from "config/font";
import { DividerLine } from "../../companyStampClock/companyStampClock.styled";
import { useTipStore } from "web-apps/company/stores/tipStore/tipStore";

type CalculateModalProps = {
  open: boolean;
  onClose: () => void;
  companyId: number;
  selectedTipIds: number[];
  onPayout: (ids: number[], type: PayoutType) => void;
  type: "manual" | "swish" | undefined;
};

const timeToSeconds = (time: string): number => {
  const [h, m, s] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

const secondsToTime = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
};

export const PayoutModal: React.FC<CalculateModalProps> = (props) => {
  const { t } = useTranslation();
  const [tipState, _tipActions] = useTipStore();
  const [tips, setTips] = useState<any[]>([]);

  useEffect(() => {
    const tips = props.selectedTipIds
      .map((id) => tipState.tips.find((t) => t.companyTipId === id))
      .filter(Boolean);

    setTips(tips);
  }, [props.selectedTipIds, tipState.tips]);

  const mergeWorkers = (companyTips: TipDetails[]): Worker[] => {
    if (!companyTips?.length) return [];
    const workerMap: Record<number, TipDetails["workers"][0]> = {};

    for (const tip of companyTips) {
      for (const worker of tip.workers ?? []) {
        const id = worker.workerId;
        if (!workerMap[id]) {
          workerMap[id] = { ...worker };
        } else {
          workerMap[id].tipAmount += worker.tipAmount;

          const existingSeconds = timeToSeconds(
            workerMap[id].workTime ?? "00:00:00"
          );
          const newSeconds = timeToSeconds(worker.workTime ?? "00:00:00");
          workerMap[id].workTime = secondsToTime(existingSeconds + newSeconds);
        }
      }
    }

    return Object.values(workerMap);
  };

  const mergedWorkers = useMemo(
    () => mergeWorkers(tips ?? []),
    [JSON.stringify(tips)]
  );

  const totals = useMemo(() => {
    const totalSeconds = mergedWorkers.reduce(
      (acc, w) => acc + timeToSeconds(w.workTime ?? "00:00:00"),
      0
    );
    const totalTipAmount = mergedWorkers.reduce(
      (acc, w) => acc + (w.tipAmount ?? 0),
      0
    );
    const totalTimeStr = secondsToTime(totalSeconds);
    const totalParsed = parseWorkTime(totalTimeStr);

    return {
      totalSeconds,
      totalTipAmount,
      totalParsed,
    };
  }, [mergedWorkers]);

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <TipModalBody>
        <CustomIcon
          color={Color.LighterDestructive}
          onClick={props.onClose}
          name={"cross"}
          style={{
            position: "absolute",
            right: 20,
            top: 20,
          }}
        >
          {t("General.Close")}
        </CustomIcon>

        <div>
          {mergedWorkers.length > 0 && (
            <>
              <H3>{t("CompanyTip.CalculatedTipsPerHour")}</H3>
              <DetailsContainer style={{ marginBottom: 40 }}>
                {mergedWorkers.map((worker: Worker) => (
                  <Fragment key={worker.workerId}>
                    <WorkerContainer>
                      <WorkerCell>
                        <Text>
                          {worker.firstName} {worker.lastName}
                        </Text>
                      </WorkerCell>
                      <WorkerCell>
                        <Text>
                          {parseWorkTime(worker.workTime).hours}
                          {t("General.HourShortened")}{" "}
                          {parseWorkTime(worker.workTime).minutes}
                          {t("General.MinutesShortened")}
                        </Text>
                      </WorkerCell>
                      <WorkerCell>
                        <Text>{worker.tipAmount} kr</Text>
                      </WorkerCell>
                    </WorkerContainer>
                    <DividerLine />
                  </Fragment>
                ))}
                <WorkerContainer style={{ fontWeight: 600, marginTop: 40 }}>
                  <WorkerCell>
                    <Text fontFamily={FontFamily.MontserratSemiBold}>
                      {t("General.Total")}
                    </Text>
                  </WorkerCell>
                  <WorkerCell>
                    <Text fontFamily={FontFamily.MontserratSemiBold}>
                      {totals.totalParsed.hours}
                      {t("General.HourShortened")} {totals.totalParsed.minutes}
                      {t("General.MinutesShortened")}
                    </Text>
                  </WorkerCell>
                  <WorkerCell>
                    <Text fontFamily={FontFamily.MontserratSemiBold}>
                      {totals.totalTipAmount} kr
                    </Text>
                  </WorkerCell>
                </WorkerContainer>
              </DetailsContainer>
            </>
          )}

          {props.type == "manual" && (
            <TipButtonStyled
              onClick={() =>
                props.onPayout(props.selectedTipIds, PayoutType.Manual)
              }
              backgroundColor={Color.ModernGreen}
            >
              {t("CompanyTip.MarkAsManuallyPaidPlural")}
            </TipButtonStyled>
          )}
          {props.type == "swish" && (
            <TipButtonStyled
              onClick={() =>
                props.onPayout(props.selectedTipIds, PayoutType.Automatic)
              }
              backgroundColor={Color.ModernGreen}
            >
              {t("CompanyTip.PayoutWithSwishPlural")}
            </TipButtonStyled>
          )}
        </div>
      </TipModalBody>
    </Modal>
  );
};
